/**
 * The acting PROJECT-USER session for the Run view.
 *
 * Two kinds of person can open a running application, and they sign in in two
 * different places. A Twasta account signs into the platform and carries an
 * httpOnly cookie; a PROJECT USER — somebody invited from the application's own
 * System administration → Users screen, who has no Twasta account at all —
 * signs into the APPLICATION and carries an opaque bearer token issued by the
 * tenant's user registry.
 *
 * This module holds the second one, for the duration of a Run session, and is
 * the only thing that decides a request is being made as a project user. The
 * client interceptor (see `client.ts`) reads it and adds three headers; the
 * backend refuses to look at a platform cookie on any request that carries
 * them (`services/project_user_access`). The two identities therefore never
 * overlap on one request, and a person who happens to hold both accounts is
 * whichever one they signed in as — never a merge of the two.
 *
 * The token itself is NOT stored here: it lives where the application's own
 * site sessions already live (`api/website`'s per-(owner, project)
 * localStorage key), because it is the same registry session. Signing into the
 * Run view therefore signs you into the same application's published pages,
 * which is the behaviour a single account should have.
 */

import { getSiteToken, setSiteToken } from './website'

export interface RunSession {
  /** Platform id of the account whose folder holds the application. */
  owner: string
  /** Application (project) name — the session is scoped to this one. */
  project: string
  /** Organization the application belongs to, from the Run URL. */
  tenant: string
}

let current: RunSession | null = null

const listeners = new Set<(s: RunSession | null) => void>()

/** Subscribe to activation / sign-out. Returns an unsubscribe. */
export function onRunSessionChange(fn: (s: RunSession | null) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function announce() {
  listeners.forEach((fn) => {
    try { fn(current) } catch { /* a listener must not break the others */ }
  })
}

/** The session in force, or null when the Run view is acting as a Twasta account. */
export function getRunSession(): RunSession | null {
  return current
}

/** True when this application is being run as a project user. */
export function isProjectUserRun(): boolean {
  return current !== null && !!runSessionToken()
}

/** The registry session token for the session in force, or ''. */
export function runSessionToken(): string {
  if (!current) return ''
  return getSiteToken(current.owner, current.project)
}

/**
 * Act as a project user for this application from now on. `token` is optional
 * — omit it when the token is already stored (a reload picking up an existing
 * session).
 */
export function startRunSession(session: RunSession, token?: string): void {
  if (token) setSiteToken(session.owner, session.project, token)
  current = session
  announce()
}

/** Stop acting as a project user. `forget` also discards the stored token. */
export function endRunSession(forget = true): void {
  const was = current
  current = null
  if (was && forget) setSiteToken(was.owner, was.project, null)
  announce()
}

/**
 * "Stop acting as this application's user in this tab."
 *
 * Signing out of the application is a CHOICE, and without recording it the Run
 * view would simply notice the session was gone and open another one — the
 * whole point of `adopt` is that it needs no password — an unusable sign-out
 * that puts you straight back where you were. Per tab, and only until it is
 * closed: a decision about this sitting, not a durable preference.
 */
const suppressKey = (project: string) => `twasta_no_adopt::${project}`

export function suppressAdopt(project: string): void {
  try { sessionStorage.setItem(suppressKey(project), '1') } catch { /* no-op */ }
}

export function isAdoptSuppressed(project: string): boolean {
  try { return sessionStorage.getItem(suppressKey(project)) === '1' } catch { return false }
}

/**
 * The headers that make a request act as a project user — read by the client
 * interceptor, and by nothing else. Empty when no session is in force, which
 * is what keeps every other request in the application byte-identical to what
 * it was before project users could sign in at all.
 */
export function runSessionHeaders(): Record<string, string> {
  const token = runSessionToken()
  if (!current || !token) return {}
  return {
    'X-Twasta-Run-As': 'project-user',
    'X-Twasta-App-Project': current.project,
    'X-Twasta-App-Owner': current.owner,
    'X-Twasta-App-Tenant': current.tenant,
    Authorization: `Bearer ${token}`,
  }
}
